/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/Layout';

// Lazy load pages
import Home from './pages/Home';
const About = lazy(() => import('./pages/About'));
const Resume = lazy(() => import('./pages/Resume'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Admin = lazy(() => import('./pages/Admin'));
const Psychometric = lazy(() => import('./pages/Psychometric'));
const Contact = lazy(() => import('./pages/Contact'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Skillset = lazy(() => import('./pages/Skillset'));
const PortfolioDetail = lazy(() => import('./pages/PortfolioDetail'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const Strategy = lazy(() => import('./pages/Strategy'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading component
const PageLoader = () => (
  <div className="min-h-[80vh] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default function App() {
  const [lang, setLang] = useState('EN');

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const AppRoutes = ({ currentLang }: { currentLang: string }) => (
    <Layout lang={currentLang} setLang={setLang}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home lang={currentLang} />} />
          <Route path="/about" element={<About lang={currentLang} />} />
          <Route path="/skillset" element={<Skillset lang={currentLang} />} />
          <Route path="/resume" element={<Resume lang={currentLang} />} />
          <Route path="/portfolio" element={<Portfolio lang={currentLang} />} />
          <Route path="/portfolio/:slug" element={<PortfolioDetail lang={currentLang} />} />
          <Route path="/blog" element={<Blog lang={currentLang} />} />
          <Route path="/blog/:slug" element={<BlogPost lang={currentLang} />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/strategy" element={<Strategy lang={currentLang} />} />
          <Route path="/strategy/:slug" element={<Strategy lang={currentLang} />} />
          <Route path="/psychometric" element={<Psychometric lang={currentLang} />} />
          <Route path="/contact" element={<Contact lang={currentLang} />} />
          <Route path="/pricing" element={<Pricing lang={currentLang} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy lang={currentLang} />} />
          <Route path="/terms" element={<Terms lang={currentLang} />} />
          <Route path="/404" element={<NotFound lang={currentLang} />} />
          <Route path="*" element={<Navigate to={currentLang === 'EN' ? "/404" : `/${currentLang.toLowerCase()}/404`} replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );

  return (
    <HelmetProvider>
      <Router>
        <Routes>
          <Route path="/ja/*" element={<AppRoutes currentLang="JP" />} />
          <Route path="/id/*" element={<AppRoutes currentLang="ID" />} />
          <Route path="/*" element={<AppRoutes currentLang="EN" />} />
        </Routes>
      </Router>
    </HelmetProvider>
  );
}
